<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">Relatório de envio</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">
            Consulte, filtre e baixe o relatório de envios das campanhas.
          </p>
        </div>
      </div>

      <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="p-6">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-500">A partir de</label>
              <input
                v-model="form.de"
                type="datetime-local"
                id="de"
                class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-500">Até</label>
              <input
                v-model="form.ate"
                type="datetime-local"
                id="ate"
                class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <div class="flex flex-col gap-1" v-if="$store.state.user.admin">
              <label class="text-sm font-medium text-gray-500">Centro de custo</label>

              <div class="relative group">
                <select
                  v-model="centrocusto"
                  id="centrocusto"
                  class="h-11 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white px-4 pr-10 text-gray-700 shadow-sm transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                >
                  <option :value="null">Todos</option>
                  <option
                    v-for="centro in centroscusto"
                    :key="centro._id"
                    :value="centro._id"
                  >
                    {{ centro.nome }}
                  </option>
                </select>

                <svg class="select-icon h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            <div class="flex items-end gap-2 md:col-span-4 lg:col-span-1">
              <button
                @click="list"
                type="button"
                :disabled="isLoading"
                class="h-11 w-full rounded-xl font-medium text-white transition"
                :class="{ 'cursor-not-allowed opacity-70': isLoading }"
                :style="{ backgroundColor: primaryColor }"
                @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                @mouseleave="$event.target.style.backgroundColor = primaryColor"
              >
                {{ isLoading ? "Gerando..." : "Gerar" }}
              </button>

              <a
                :href="downloadUrl"
                class="flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 font-medium text-gray-700 transition hover:bg-gray-50"
              >
                <svg
                  class="h-5 w-5 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 3v12m0 0l-3.5-3.5M12 15l3.5-3.5M4 21h16"
                  />
                </svg>
                Download
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <span class="text-sm font-medium text-gray-500">Total</span>
          <p class="mt-2 text-3xl text-gray-900">{{ totalEnvios }}</p>
        </div>

        <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <span class="text-sm font-medium text-gray-500">Total enviados para fila</span>
          <p class="mt-2 text-3xl text-green-700">{{ totalEnviados }}</p>
        </div>

        <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <span class="text-sm font-medium text-gray-500">Total não enviados</span>
          <p class="mt-2 text-3xl text-red-700">{{ totalNaoEnviados }}</p>
        </div>
      </div>

      <div
        v-if="erro"
        class="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
      >
        {{ erro }}
      </div>

      <div
        v-if="$store.state.user.neon"
        class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
      >
        <div class="p-6">
          <h2 class="text-lg font-medium text-gray-900">Resumo de status</h2>

          <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div class="rounded-2xl bg-gray-50 p-4">
              <p class="text-sm font-medium text-gray-500">Pendente</p>
              <p class="mt-1 text-2xl text-gray-900">
                {{ campanhaStatus && campanhaStatus.pendente ? campanhaStatus.pendente : 0 }}
              </p>
            </div>

            <div class="rounded-2xl bg-gray-50 p-4">
              <p class="text-sm font-medium text-gray-500">Número inválido</p>
              <p class="mt-1 text-2xl text-gray-900">
                {{ campanhaStatus && campanhaStatus.numeroInvalido ? campanhaStatus.numeroInvalido : 0 }}
              </p>
            </div>

            <div class="rounded-2xl bg-green-50 p-4">
              <p class="text-sm font-medium text-green-700">{{ $store.getters.rotuloEnvio('Enviados', 'Enviados para fila') }}</p>
              <p class="mt-1 text-2xl text-green-700">
                {{ campanhaStatus && campanhaStatus.enviado ? campanhaStatus.enviado : 0 }}
              </p>
            </div>

            <div class="rounded-2xl bg-green-50 p-4">
              <p class="text-sm font-medium text-green-700">Entregues</p>
              <p class="mt-1 text-2xl text-green-700">
                {{ campanhaStatus && campanhaStatus.entregues ? campanhaStatus.entregues : 0 }}
              </p>
            </div>

            <div class="rounded-2xl bg-blue-50 p-4">
              <p class="text-sm font-medium text-blue-700">Visualizado</p>
              <p class="mt-1 text-2xl text-blue-700">
                {{ campanhaStatus && campanhaStatus.visualizado ? campanhaStatus.visualizado : 0 }}
              </p>
            </div>

            <div class="rounded-2xl bg-red-50 p-4">
              <p class="text-sm font-medium text-red-700">Não enviado</p>
              <p class="mt-1 text-2xl text-red-700">
                {{ campanhaStatus && campanhaStatus.naoEnviado ? campanhaStatus.naoEnviado : 0 }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full min-w-max text-sm">
            <thead class="border-b bg-gray-100 tracking-wider text-gray-600">
              <tr>
                <th class="px-6 py-4 text-left font-medium">Campanha</th>

                <th
                  v-if="$store.state.user.lzv2.centrocusto"
                  class="px-6 py-4 text-left font-medium"
                >
                  Centro de Custo
                </th>

                <th class="px-6 py-4 text-left font-medium">Data</th>
                <th class="px-6 py-4 text-center font-medium">Total</th>
                <th class="px-6 py-4 text-center font-medium">{{ $store.getters.rotuloEnvio('Enviado', 'Enviado para fila') }}</th>
                <th class="px-6 py-4 text-center font-medium">Não enviado</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="el in campanhas"
                :key="el._id"
                class="border-b border-gray-100 transition hover:bg-slate-50"
              >
                <td class="px-6 py-5">
                  <div class="flex items-center gap-4">
                    <div class="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-sm font-semibold text-blue-700">
                      {{ (el.nome || "").slice(0, 2).toUpperCase() }}
                    </div>

                    <div class="flex flex-col">
                      <span class="font-medium text-gray-800">
                        {{ el.nome || "-" }}
                      </span>
                      <span class="text-xs text-gray-400">Campanha enviada</span>
                    </div>
                  </div>
                </td>

                <td
                  v-if="$store.state.user.lzv2.centrocusto"
                  class="px-6 font-medium text-gray-700"
                >
                  {{ el.centrocusto && el.centrocusto.nome ? el.centrocusto.nome : "-" }}
                </td>

                <td class="px-6 font-medium text-gray-700">
                  {{ moment(el.dataHoraDisparo).format("DD/MM/YYYY HH:mm") }}
                </td>

                <td class="px-6 text-center font-medium text-gray-700">
                  {{ el.total || 0 }}
                </td>

                <td class="px-6 text-center">
                  <span class="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                    {{ el.totalEnviado || 0 }}
                  </span>
                </td>

                <td class="px-6 text-center">
                  <span class="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700">
                    {{ el.totalFalhas || 0 }}
                  </span>
                </td>
              </tr>

              <tr v-if="campanhas.length === 0">
                <td
                  :colspan="$store.state.user.lzv2.centrocusto ? 6 : 5"
                  class="px-6 py-12 text-center text-sm text-gray-400"
                >
                  Nenhum registro encontrado com os filtros atuais.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <AppPagination
        :current-page="page"
        :total-pages="totalPages"
        :total-items="total"
        :page-size="perPage"
        item-label="envios"
        @paginate="setPage"
      />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import AppPagination from "@/components/AppPagination.vue";
import { colorMixin } from '@/utils/colorMixin';

export default {
  mixins: [colorMixin],
  components: {
    AppPagination
  },
  data() {
    return {
      route: "relatorio-envio",
      apilz:
        window.location.hostname === "uno.trestto.com.br"
          ? "https://uno.trestto.com.br/api"
          : process.env.VUE_APP_API,
      moment,
      campanhas: [],
      centroscusto: [],
      centrocusto: null,
      page: 1,
      perPage: 25,
      form: {
        busca: "",
        de: moment().startOf("day").format("YYYY-MM-DDTHH:mm"),
        ate: moment().endOf("day").format("YYYY-MM-DDTHH:mm")
      },
      total: 0,
      totalEnvios: 0,
      totalEnviados: 0,
      totalNaoEnviados: 0,
      campanhaStatus: null,
      isLoading: false,
      erro: ""
    };
  },
  computed: {
    downloadUrl() {
      const params = new URLSearchParams({
        token: this.$store.state.token,
        de: this.form.de || "",
        ate: this.form.ate || "",
        centrocusto: this.centrocusto || ""
      });

      return `${this.apilz}/v1/campanhasLZV2/relatorio-envio/download?${params.toString()}`;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.perPage));
    }
  },

  methods: {
    async setPage(page) {
      this.page = page;
      this.isLoading = true;
      this.erro = "";

      try {
        const skip = (page - 1) * this.perPage;
        const limit = this.perPage;

        const listReq = await this.$http.post("/v1/campanhasLZV2/relatorio-envio", {
          de: this.form.de,
          ate: this.form.ate,
          centrocusto: this.centrocusto,
          limit,
          skip
        });

        this.campanhas = listReq.data.data || [];
        this.total = listReq.data.total || 0;
        this.totalEnvios = listReq.data.relatorio ? listReq.data.relatorio.total : 0;
        this.totalEnviados = listReq.data.relatorio ? listReq.data.relatorio.totalEnviados : 0;
        this.totalNaoEnviados = listReq.data.relatorio ? listReq.data.relatorio.totalNaoEnviados : 0;

        if (this.$store.state.user.neon) {
          const resp = await this.$http.post("/v1/campanhasLZV2/resumostatus", {
            de: this.form.de,
            ate: this.form.ate,
            centrocusto: this.centrocusto
          });

          this.campanhaStatus = resp.data.result || null;
        }
      } catch (err) {
        this.erro = "Nao foi possivel gerar o relatorio agora. Tente reduzir o periodo ou tente novamente em alguns instantes.";
        this.$vToastify.error(this.erro);
      } finally {
        this.isLoading = false;
      }
    },

    async list() {
      await this.setPage(1);
    }
  },

  async beforeMount() {
    const centroscustoReq = await this.$http.post("/v1/centrocusto/list", {
      all: true
    });

    this.centroscusto = centroscustoReq.data.data || [];

    if (this.$store.state.user.centrocusto) {
      this.centrocusto = this.$store.state.user.centrocusto;
    }

    await this.list();
  }
};
</script>

<style scoped>
.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  pointer-events: none;
  color: #9ca3af;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

#centrocusto::-ms-expand {
  display: none;
}

#centrocusto {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none !important;
}

.group:focus-within .select-icon {
  color: #6366f1;
  transform: translateY(-50%) rotate(180deg);
}
</style>
